import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  Toast,
  ToastDescription,
  ToastDismissSurface,
  ToastIcon,
  ToastProvider,
  ToastTitle,
  ToastViewport,
  type ToastPayload,
  type ToastVariant,
} from '@/components/ui/toast';
import { describeError, isApiError } from '@/lib/apiError';
import { CopyableId } from '@/components/CopyableId';

// -----------------------------------------------------------------------------
// Toast store + renderer.
//
// A tiny context rather than a global singleton so tests can render a tree and
// assert on what was shown without leaking state between cases.
// -----------------------------------------------------------------------------

type ToastContextValue = {
  toast(payload: Omit<ToastPayload, 'id'>): void;
  /** Shorthand for the success case, which is most of the call sites. */
  success(title: string, description?: string): void;
  /**
   * Turns any thrown value into a failure toast with staff-facing copy and the
   * request id attached. Every mutation's `onError` calls this.
   */
  failure(error: unknown, fallbackTitle?: string): void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

/** Errors stay until dismissed; a success can disappear on its own. */
const DURATION: Record<ToastVariant, number> = {
  success: 4000,
  info: 5000,
  warning: 8000,
  error: Number.POSITIVE_INFINITY,
};

export function ToasterProvider({ children }: { children: ReactNode }): JSX.Element {
  const [toasts, setToasts] = useState<ToastPayload[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((item) => item.id !== id));
  }, []);

  const toast = useCallback((payload: Omit<ToastPayload, 'id'>) => {
    const id =
      typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `toast-${Date.now()}-${Math.random()}`;
    // Cap the stack. Ten toasts on screen is not information, it is a wall.
    setToasts((current) => [...current.slice(-4), { ...payload, id }]);
  }, []);

  const success = useCallback(
    (title: string, description?: string) => {
      toast({ variant: 'success', title, ...(description ? { description } : {}) });
    },
    [toast],
  );

  const failure = useCallback(
    (error: unknown, fallbackTitle?: string) => {
      const described = describeError(error);
      toast({
        variant: 'error',
        title: fallbackTitle ?? described.title,
        description: described.description,
        requestId: isApiError(error) ? error.requestId : null,
      });
    },
    [toast],
  );

  const value = useMemo<ToastContextValue>(
    () => ({ toast, success, failure }),
    [toast, success, failure],
  );

  return (
    <ToastContext.Provider value={value}>
      <ToastProvider swipeDirection="right">
        {children}
        {toasts.map((item) => (
          <Toast
            key={item.id}
            variant={item.variant}
            duration={DURATION[item.variant]}
            onOpenChange={(open) => {
              if (!open) dismiss(item.id);
            }}
          >
            {/* The dismiss surface is first and z-0 so the content above it
                stays clickable - the "Details" disclosure has to remain
                usable inside a click-to-dismiss card. */}
            <ToastDismissSurface />

            <ToastIcon variant={item.variant} />

            <div className="relative z-10 flex min-w-0 flex-1 flex-col gap-0.5">
              <ToastTitle>{item.title}</ToastTitle>
              {item.description ? <ToastDescription>{item.description}</ToastDescription> : null}
              {item.requestId ? (
                <CopyableId className="mt-1" label="Request id" value={item.requestId} />
              ) : null}
            </div>
          </Toast>
        ))}
        <ToastViewport />
      </ToastProvider>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside <ToasterProvider>.');
  return context;
}
